import { GamePage, PlayApiProvider } from '@mogzo/play';
import {
  type FeedSort,
  GENRE_NAMES,
  type ListingImage,
  RATING_NAMES,
  ratingOf,
} from '@mogzo/shared';
import { Check, ChevronRight, Play, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { PostCard } from '../../components/community/PostCard';
import { SignInForPosts, useCommunityPosts } from '../../components/community/PostsPanel';
import { CommunityShell, NoticeCard } from '../../components/community/Shell';
import { GameCover, GameIcon } from '../../components/games/GameArt';
import { launchesFullScreen, useScreenSize } from '../../components/play/launch';
import { PhonePlayer } from '../../components/play/PhonePlayer';
import { Alert, buttonClass, PageSpinner } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { leaveCommunity } from '../../lib/communities';
import { fetchLiveGame, joinCommunity, type LiveGame, livePlayApi } from '../../lib/games';
import { useSession } from '../../lib/session';
import { Composer, chipClass, SORTS } from '../community/TestCommunity';
import { NotFound } from '../NotFound';

type State =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'failed' }
  | { status: 'ready'; data: LiveGame };

/** Loads the live game at the address; strangers get the same "Page not found" as a typo. */
export function useLiveGame(slug: string): State {
  const { me } = useSession();
  const [state, setState] = useState<State>({ status: 'loading' });
  useEffect(() => {
    if (me === undefined) return;
    let active = true;
    fetchLiveGame(slug)
      .then((data) => active && setState({ status: 'ready', data }))
      .catch((error) => {
        if (!active) return;
        const hidden = error instanceof ApiError && [401, 403, 404].includes(error.status);
        setState({ status: hidden ? 'missing' : 'failed' });
      });
    return () => {
      active = false;
    };
  }, [slug, me]);
  return state;
}

/** Strangers get the app's own 404. */
export const Missing = NotFound;

export function Failed() {
  return (
    <CommunityShell>
      <NoticeCard title="Couldn’t load this page">
        <p>MOGZO didn’t answer. Reload the page to try again.</p>
      </NoticeCard>
    </CommunityShell>
  );
}

const beforeLaunchText = (data: LiveGame) =>
  data.suspended
    ? 'MOGZO has suspended this game. Only its developer and MOGZO staff can see it.'
    : 'Only its developer and MOGZO staff can see this page until MOGZO opens to players. Everyone else gets “Page not found”.';

/** Who can see the page before MOGZO opens to players. `quiet`: one line, for the play page. */
export function BeforeLaunch({ data, quiet = false }: { data: LiveGame; quiet?: boolean }) {
  if (quiet) {
    return (
      <p role="status" className="text-[13px] leading-5 text-muted">
        {beforeLaunchText(data)}
      </p>
    );
  }
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl bg-ink px-4 py-3 text-sm text-white"
    >
      <span className="flex-[1_1_320px]">{beforeLaunchText(data)}</span>
      <span className="font-mono text-[13px] text-white/75">Live · {data.version} · live data</span>
    </div>
  );
}

const GAME_TABS = [
  { value: 'posts', label: 'Posts' },
  { value: 'about', label: 'About' },
] as const;

/**
 * mogzo.com/games/<address>: a live game's page, which is its community (M29): Play and Join on
 * top, its posts, and its store listing as the About tab. Follows the coming-soon switch.
 */
export function LiveGamePage() {
  const { slug = '' } = useParams();
  const state = useLiveGame(slug);
  const { me } = useSession();
  const navigate = useNavigate();
  const size = useScreenSize();
  const [playing, setPlaying] = useState(false);
  const api = useMemo(() => livePlayApi(slug), [slug]);

  if (state.status === 'loading') return <PageSpinner />;
  if (state.status === 'missing') return <Missing />;
  if (state.status === 'failed') return <Failed />;
  const { data } = state;
  const phone = size === 'phone';

  // Touch screens (phones and tablets) play full screen; computers open the play page.
  function play() {
    if (!me) navigate(`/sign-in?next=${encodeURIComponent(`/games/${slug}`)}`);
    else if (launchesFullScreen()) setPlaying(true);
    else navigate(`/games/${slug}/play`);
  }

  return (
    <PlayApiProvider api={api}>
      <CommunityShell title={data.game.name}>
        <GameCommunity data={data} phone={phone} onPlay={play} />
      </CommunityShell>
      {playing && (
        <PhonePlayer
          url={data.playUrl}
          name={data.game.name}
          projectId={data.game.id}
          orientation={data.listing.orientation}
          launch={{}}
          isDeveloper={Boolean(data.viewer?.isDeveloper)}
          onExit={() => setPlaying(false)}
        />
      )}
    </PlayApiProvider>
  );
}

function GameCommunity({
  data,
  phone,
  onPlay,
}: {
  data: LiveGame;
  phone: boolean;
  onPlay: () => void;
}) {
  const { me, comingSoon } = useSession();
  const { game, listing, community } = data;
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'about' ? 'about' : 'posts';
  const membership = useMembership(data);
  const { joined, memberCount, error } = membership;
  const images = useMemo(() => new Map(data.images.map((image) => [image.id, image])), [data]);
  const card = gameArt(data, images);
  const facts = [
    listing.genre && GENRE_NAMES[listing.genre],
    listing.devices.length === 2
      ? 'Phone and computer'
      : listing.devices[0] === 'phone'
        ? 'Phone'
        : listing.devices[0] === 'computer'
          ? 'Computer'
          : null,
    RATING_NAMES[ratingOf(listing.rating)],
  ].filter(Boolean) as string[];

  function showTab(next: 'posts' | 'about') {
    setParams(next === 'posts' ? {} : { tab: next }, { replace: true });
  }

  const joinButton = canJoin(data) ? <JoinButton data={data} membership={membership} /> : null;

  return (
    <>
      <section aria-labelledby="game-name" className="border-b border-divider bg-card">
        <GameCover game={card} className="h-[150px] sm:h-[220px]" />
        <div className="mx-auto w-full max-w-[1180px] px-4 sm:px-6">
          <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
            <span className="relative z-10 -mt-9 sm:-mt-12">
              <GameIcon
                game={card}
                className="size-[72px] rounded-[18px] border-4 border-card text-2xl shadow-[0_2px_10px_rgba(27,21,47,0.16)] sm:size-[104px] sm:rounded-[26px] sm:text-4xl"
              />
            </span>
            <div className="min-w-0 flex-[1_1_220px] pb-1">
              <h1
                id="game-name"
                className="text-2xl leading-7 font-bold tracking-[-0.015em] sm:text-[30px] sm:leading-9"
              >
                {game.name}
              </h1>
              <p className="mt-0.5 text-sm text-muted">
                by {listing.developer || 'its developer'}
                {community ? ` · ${memberCount} member${memberCount === 1 ? '' : 's'}` : ''}
              </p>
            </div>
            <div className="flex w-full gap-2.5 pb-1 sm:w-auto">
              <button
                type="button"
                onClick={onPlay}
                className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-brand px-6 text-[15px] font-semibold text-ink hover:bg-brand-hover sm:flex-none"
              >
                <Play size={14} fill="currentColor" aria-hidden="true" />
                Play
              </button>
              {joinButton && (
                <span className="flex flex-1 sm:flex-none [&>*]:w-full">{joinButton}</span>
              )}
            </div>
          </div>
          {facts.length > 0 && (
            <ul aria-label="About the game" className="mt-3 flex flex-wrap gap-1.5">
              {facts.map((fact) => (
                <li
                  key={fact}
                  className="inline-flex h-7 items-center rounded-full bg-field px-2.5 text-[13px] font-medium"
                >
                  {fact}
                </li>
              ))}
            </ul>
          )}
          {error && (
            <div className="mt-3">
              <Alert>{error}</Alert>
            </div>
          )}
          <nav aria-label="Game sections" className="mt-2 flex gap-1 sm:mt-3">
            {GAME_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                aria-current={tab === t.value ? 'page' : undefined}
                onClick={() => showTab(t.value)}
                className={`border-b-[3px] px-3 py-3 sm:px-4 ${tab === t.value ? 'border-ink font-bold' : 'border-transparent font-semibold text-muted hover:text-ink'}`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1180px] px-3 pt-4 pb-16 sm:px-6 sm:pt-5">
        {data.viewer && (comingSoon || data.suspended) && (
          <div className="mb-4">
            <BeforeLaunch data={data} />
          </div>
        )}
        {tab === 'posts' ? (
          <GamePosts data={data} joined={joined} images={images} onAbout={() => showTab('about')} />
        ) : (
          <GamePage
            listing={listing}
            images={images}
            viewer={me?.displayName ?? ''}
            phone={phone}
            aboutOnly
          />
        )}
      </div>
    </>
  );
}

/** The Posts tab: a short pitch for people who haven't joined, then the community's posts. */
function GamePosts({
  data,
  joined,
  images,
  onAbout,
}: {
  data: LiveGame;
  joined: boolean;
  images: Map<string, ListingImage>;
  onAbout: () => void;
}) {
  const { me, comingSoon } = useSession();
  const { game, listing, community } = data;
  const [sort, setSort] = useState<FeedSort>('hot');
  // Posts are for people signed in; someone signed out is asked to sign in instead.
  const readable = Boolean(community && me);
  const { posts, setPosts, hasMore, failed, loadMore } = useCommunityPosts(
    readable ? (community?.id ?? null) : null,
    sort,
  );
  const composer = useRef<HTMLTextAreaElement>(null);
  const shots = listing.screenshotIds
    .map((id) => images.get(id)?.url)
    .filter((shot): shot is string => Boolean(shot))
    .slice(0, 3);
  const author = {
    name: game.name,
    path: `/games/${game.slug}`,
    projectId: game.id,
    developerName: listing.developer || null,
  };

  return (
    <main aria-label="Posts" className="mx-auto flex w-full max-w-[760px] flex-col gap-3.5">
      {!joined && (
        <section
          aria-labelledby="new-here"
          className="rounded-[18px] border border-line bg-card px-4 py-4 sm:px-5"
        >
          <h2 id="new-here" className="text-[17px] font-bold">
            New to {game.name}?
          </h2>
          {shots.length > 0 && (
            <div className="mt-3 flex gap-2.5 overflow-x-auto [scrollbar-width:none]">
              {shots.map((shot) => (
                <img
                  key={shot}
                  src={shot}
                  alt=""
                  className="h-[110px] w-auto shrink-0 rounded-xl object-cover"
                />
              ))}
            </div>
          )}
          {listing.shortDescription && (
            <p className="mt-3 leading-relaxed text-plum">{listing.shortDescription}</p>
          )}
          <button
            type="button"
            onClick={onAbout}
            className="mt-2 inline-flex items-center gap-1 text-sm font-semibold underline decoration-brand decoration-2 underline-offset-4"
          >
            More about the game
            <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </section>
      )}

      {joined && community && me && comingSoon && (
        <p className="rounded-[18px] border border-line bg-card px-4 py-3 text-sm text-muted">
          Posting opens when MOGZO launches.
        </p>
      )}
      {joined && community && me && !comingSoon && (
        <Composer
          ref={composer}
          communityId={community.id}
          viewerName={me.displayName}
          placeholder="Share a run, a tip or a question"
          open
          onPosted={(post) => setPosts((current) => [post, ...(current ?? [])])}
        />
      )}

      {community && (
        <fieldset className="flex flex-wrap gap-2">
          <legend className="sr-only">Sort posts</legend>
          {SORTS.map((s) => (
            <button
              key={s.value}
              type="button"
              aria-pressed={sort === s.value}
              onClick={() => setSort(s.value)}
              className={chipClass(sort === s.value, 'md')}
            >
              {s.label}
            </button>
          ))}
        </fieldset>
      )}
      {failed && <Alert>Couldn’t load posts. Reload the page to try again.</Alert>}
      {community && !me && <SignInForPosts next={`/games/${game.slug}`} />}
      {readable && !posts && !failed && (
        <p role="status" className="py-6 text-center text-sm text-muted">
          Loading posts…
        </p>
      )}
      {(!community || posts?.length === 0) && (
        <p className="rounded-[18px] border border-dashed border-line px-5 py-8 text-center text-muted">
          No posts yet.
        </p>
      )}
      {posts?.map((post) => (
        <PostCard key={post.id} post={post} community={author} onLaunch={() => undefined} />
      ))}
      {hasMore && (
        <button type="button" onClick={loadMore} className={buttonClass('secondary')}>
          Show more posts
        </button>
      )}
    </main>
  );
}

/** The game's icon and cover, from its live store listing. */
export function gameArt(data: LiveGame, images: Map<string, ListingImage>) {
  const url = (id: string | null) => (id ? images.get(id)?.url : undefined) ?? null;
  return {
    name: data.game.name,
    iconUrl: url(data.listing.iconId),
    coverUrl: url(data.listing.coverId),
  };
}

/** Whether the page offers Join: there's a community, and this isn't its developer (always in). */
export const canJoin = (data: LiveGame) => Boolean(data.community && !data.viewer?.isDeveloper);

export type Membership = ReturnType<typeof useMembership>;

/** Joining and leaving the game's community, with its member count. */
export function useMembership(data: LiveGame) {
  const { community } = data;
  const [joined, setJoined] = useState(Boolean(data.viewer?.joined));
  const [memberCount, setMemberCount] = useState(community?.memberCount ?? 0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    if (!community) return;
    setBusy(true);
    setError(null);
    try {
      if (joined) {
        if (!window.confirm(`Leave ${data.game.name}’s community?`)) return;
        await leaveCommunity(community.id);
        setJoined(false);
        setMemberCount((n) => Math.max(n - 1, 0));
      } else {
        const answer = await joinCommunity(community.id);
        setJoined(true);
        setMemberCount(answer.memberCount);
      }
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return { joined, memberCount, error, busy, toggle };
}

const joinClass =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-line bg-card px-5 text-[15px] font-semibold hover:border-muted disabled:opacity-60';

/** Join (or Joined) for the game's community; signing in first for someone signed out. */
export function JoinButton({ data, membership }: { data: LiveGame; membership: Membership }) {
  const { me } = useSession();
  const { joined, busy, toggle } = membership;
  if (!me) {
    return (
      <Link
        to={`/sign-in?next=${encodeURIComponent(`/games/${data.game.slug}`)}`}
        className={joinClass}
      >
        <Plus size={16} strokeWidth={2.4} aria-hidden="true" />
        Join
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={joined}
      className={joinClass}
    >
      {joined ? (
        <Check size={16} strokeWidth={2.4} aria-hidden="true" />
      ) : (
        <Plus size={16} strokeWidth={2.4} aria-hidden="true" />
      )}
      {joined ? 'Joined' : 'Join'}
    </button>
  );
}
